// src/router/LegacyRouteRedirect.tsx
//
// M1 — a redirect that PRESERVES the query string.
//
// The Unibox reads its whole view state from the URL: `?medium=`, `?threadId=`,
// `?messageId=`, `?unreadOnly=`, `?status=`. A plain `<Navigate to="/messages">`
// would drop all of that, so every existing bookmark and every command-palette
// deep link would land on an unfiltered inbox instead of the thread the user
// asked for. That is exactly the kind of silent breakage this pass is meant to
// avoid, so the search string is carried across verbatim.
//
// `defaultSearch` covers the case where the OLD path carried meaning the new
// one has to be told explicitly — `/sms` means "the SMS view", which the
// canonical route expresses as `?medium=sms`. An incoming search string wins,
// so `/sms?medium=email` is honoured rather than overridden.

import { Navigate, useLocation, useParams } from 'react-router-dom';

interface Props {
  /** Canonical path to send the user to. */
  to: string;
  /** Applied only when the incoming URL carries no query string of its own. */
  defaultSearch?: string;
}

export default function LegacyRouteRedirect({ to, defaultSearch }: Props) {
  const { search } = useLocation();
  const effective = search || (defaultSearch ? `?${defaultSearch}` : '');
  return <Navigate to={`${to}${effective}`} replace />;
}

/**
 * `/companies/:id` → `/contacts/companies/:id`.
 *
 * Company detail moved under the consolidated Contacts path so the suite
 * shell's prefix-matched rail lights "Contacts" while a company is open —
 * see `ContactsHubPage` for why the tab is a path and not a query parameter.
 * The id has to survive the hop, which a static `to` cannot express, so this
 * is a param-aware sibling of the component above rather than another prop on
 * it. Query string is carried across for the same reason as above.
 */
export function LegacyCompanyDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`/contacts/companies/${id}${search}`} replace />;
}
